import type { UseQueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/_config/lib/axios/api-client';
import { handleResponseThrowError } from '@/_utils/services';
import { HEADER_TOTAL_COUNT } from '@/_config/lib/axios/interceptors';
import { DEFAULT_LIMIT_REQUEST } from '@/Constants';
import { GET_ANALYTICS } from './constants';
import type { EventAnalytics } from '../types';

export type GetAnalyticsParams = {
  limit?: number;
  page?: number;
  startDate?: Date;
  endDate?: Date;
  deliverymanId?: string;
  deliveryId?: string;
  cityId?: string;
  placeId?: string;
  event?: string;
  sort?: string;
};

const getAnalytics = (params: GetAnalyticsParams) => {
  const {
    page,
    limit,
    cityId,
    deliveryId,
    deliverymanId,
    endDate,
    event,
    placeId,
    sort,
    startDate,
  } = params;

  const newPage = page || 1;
  const newLimit = limit || DEFAULT_LIMIT_REQUEST;
  const offset = (newPage - 1) * newLimit;

  return apiClient
    .get<EventAnalytics[]>(`/analytic`, {
      params: {
        limit: newLimit,
        offset,
        cityId,
        deliveryId,
        deliverymanId,
        endDate,
        event,
        placeId,
        sort,
        startDate,
      },
    })
    .then(response => ({
      data: response.data,
      totalCount: parseInt(response.headers[HEADER_TOTAL_COUNT] || '', 10),
    }))
    .catch(handleResponseThrowError());
};

type GetPlacesOpenResponse = Awaited<ReturnType<typeof getAnalytics>>;

export const useGetAnalytics = (
  params: GetAnalyticsParams,
  options: Omit<
    UseQueryOptions<
      GetPlacesOpenResponse,
      unknown,
      GetPlacesOpenResponse,
      (string | GetAnalyticsParams)[]
    >,
    'queryKey' | 'queryFn'
  > = {},
) => {
  return useQuery({
    queryKey: [GET_ANALYTICS, params],
    queryFn: () => getAnalytics(params),
    ...options,
  });
};
